import { useState, useCallback, useRef, useEffect, CSSProperties } from 'react';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import isNil from 'lodash/isNil';
import Box from '@mui/material/Box';
import ProgressSpinner from 'components/ProgressSpinner/ProgressSpinner';
import { StyledDocument, StyledPage } from './PreviewPDFModal.style';

export interface PreviewParameters {
    scale: number;
    rotate: number;
    fitToScreen?: 'width' | 'height';
}

export interface PDFViewerProps {
    url: string;
    className?: string;
    onLoad: () => void;
    onError: () => void;
    parameters: PreviewParameters;
    style?: CSSProperties;
}

interface PDFViewerState {
    numPages: number;
    pageNumber: number;
    height?: number;
    width?: number;
}

export const PDFViewer = ({ url, className, onLoad, onError, parameters, style }: PDFViewerProps) => {
    const [state, setState] = useState<PDFViewerState>({
        numPages: 0,
        pageNumber: 1
    });

    const incPage = useCallback(
        () =>
            setState(s => ({
                ...s,
                pageNumber: s.pageNumber + 1
            })),
        []
    );
    const decPage = useCallback(
        () =>
            setState(s => ({
                ...s,
                pageNumber: s.pageNumber - 1
            })),
        []
    );

    const onDocumentLoadSuccess = (pdf: any) => {
        setState(s => ({
            ...s,
            pageNumber: 1,
            numPages: pdf.numPages
        }));
        onLoad();
    };

    const { numPages, pageNumber, height, width } = state;

    const { scale, fitToScreen, rotate } = parameters;

    const ref = useRef<any>();

    useEffect(() => {
        if (ref.current && fitToScreen) {
            const div = ref.current as HTMLDivElement;
            if (div) {
                const parentDiv = div.parentElement!.parentElement as HTMLDivElement;
                const newState: Partial<PDFViewerState> = {
                    width: undefined,
                    height: undefined
                };

                if (fitToScreen === 'width') {
                    newState.width = parentDiv.offsetWidth;
                } else {
                    newState.height = parentDiv.offsetHeight;
                }

                setState(s => ({
                    ...s,
                    ...newState
                }));
            }
        }
    }, [fitToScreen]);

    return (
        <Box
            ref={ref}
            className={className}
            style={style}
            sx={{
                '& > div:last-child': {
                    position: 'absolute',
                    bottom: 64,
                    right: 72,
                    opacity: 0,
                    transition: theme => theme.transitions.create('opacity')
                },
                '&:hover': {
                    '& > div:last-child': {
                        opacity: 1
                    }
                }
            }}
        >
            <StyledDocument
                file={url}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onError}
                loading={<ProgressSpinner />}
            >
                <StyledPage pageNumber={pageNumber} scale={scale} height={height} width={width} rotate={rotate} />
            </StyledDocument>
            <div>
                <Paper>
                    <Button disabled={(pageNumber ?? 0) <= 1} onClick={decPage}>
                        Prev
                    </Button>
                    <Button disabled={isNil(pageNumber) || pageNumber >= numPages - 1} onClick={incPage}>
                        Next
                    </Button>
                </Paper>
            </div>
        </Box>
    );
};
